import type { ParsedUrlQuery } from "node:querystring";
import { format, parseISO } from "date-fns";

import { addTruthyParamsToUrl } from "@zapier/url-utils";

import { AlertType } from "@zapier/reporting/src/graphql-types";
import {
  ActivityFilterType,
  AlertFilterTypes,
  HistoryFiltersTypes,
  Range,
  StatusFilterType,
} from "@zapier/reporting/src/types";
import { getDateRangeFromQuery } from "../getDateRangeFromQuery";

function getArrayParamFromQuery(
  key: string,
  query: ParsedUrlQuery,
  path: string
) {
  const value =
    query[key] || path.match(new RegExp(`[&?]${key}=([^&#]*)`))?.[1];

  if (Array.isArray(value)) {
    return value;
  }

  if (value?.includes("|")) {
    return value.split("|");
  }
  // Url encoded "|""
  if (value?.includes("%7C")) {
    return value.split("%7C");
  }

  if (value?.includes(",")) {
    return value.split(",");
  }
  // Url encoded ","
  if (value?.includes("%2C")) {
    return value.split("%2C");
  }

  return value ? [value] : [];
}

export function getParamFromQuery(
  key: string,
  query: ParsedUrlQuery,
  path: string
) {
  const value =
    query[key] || path.match(new RegExp(`[&?]${key}=([^&#]*)`))?.[1];

  if (Array.isArray(value)) {
    return value[0];
  }

  return value || undefined;
}

export function getFiltersFromParams(
  query: ParsedUrlQuery,
  path: string,
  billingPeriod?: Partial<Range<string>>
): HistoryFiltersTypes {
  return {
    actions: getArrayParamFromQuery("action", query, path)
      // @ts-ignore
      .map((item) => ActivityFilterType[item])
      .filter(Boolean),
    apps: getArrayParamFromQuery("app", query, path),
    channel: getParamFromQuery("channel", query, path),
    channelError: getParamFromQuery("channelError", query, path),
    dateFilter: getDateRangeFromQuery(
      {
        end_datetime: getParamFromQuery("end_datetime", query, path),
        start_datetime: getParamFromQuery("start_datetime", query, path),
      },
      billingPeriod
    ),
    folderIds: getArrayParamFromQuery("folderId", query, path),
    nodeIds: getArrayParamFromQuery("root_id", query, path),
    ownerIds: getArrayParamFromQuery("owner", query, path),
    page: parseInt(getParamFromQuery("page", query, path) || "") || 1,
    search: getParamFromQuery("query", query, path),
    status: getArrayParamFromQuery("status", query, path)
      // @ts-ignore
      .map((item) => StatusFilterType[item])
      .filter(Boolean),
  };
}

export function getAlertFiltersFromParams(
  query: ParsedUrlQuery,
  path: string
): AlertFilterTypes {
  return {
    dateFilter: getDateRangeFromQuery({
      end_datetime: getParamFromQuery("end_datetime", query, path),
      start_datetime: getParamFromQuery("start_datetime", query, path),
    }),
    page: parseInt(getParamFromQuery("page", query, path) || "") || 1,
    alertTypes: getArrayParamFromQuery("alertTypes", query, path)
      // @ts-ignore
      .map((item) => AlertType[item])
      .filter(Boolean),
  };
}

function convertAllStatusToNone(status: StatusFilterType[] | undefined) {
  if (!status) return [];
  return status.length === Object.keys(StatusFilterType).length ? [] : status;
}

function convertAllAlertStatusToNone(alert: AlertType[] | undefined) {
  if (!alert) return [];
  return alert.length === Object.keys(AlertType).length ? [] : alert;
}

export function getUrlFromParams(
  url: string,
  filters: Partial<HistoryFiltersTypes>,
  query: any = {},
  paramsOnly = false
): string {
  const fullUrl = addTruthyParamsToUrl(url, {
    ...query,
    action: filters.actions?.join(","),
    app: filters.apps?.join(","),
    channel: filters.channel,
    channelError: filters.channelError,
    end_datetime: getDateWithTime(
      filters.dateFilter?.to,
      query?.end_datetime,
      filters?.dateFilter?.hasChange || false
    ),
    folderId: filters.folderIds?.join(","),
    // Clear node_id filter since it will be changed to root_id
    node_id: undefined,
    owner: filters.ownerIds?.join(","),
    page: filters.page === 1 ? undefined : filters.page,
    query: filters.search,
    root_id: Array.isArray(filters.nodeIds) && filters.nodeIds?.join(","),
    start_datetime: getDateWithTime(
      filters.dateFilter?.from,
      query?.start_datetime,
      filters?.dateFilter?.hasChange || false
    ),
    status: convertAllStatusToNone(filters.status).join(","),
    // Run Log embed gets zapId as a url query param. We don't need to send that
    // back to the parent when we indicate there is a route change
    zapId: undefined,
  });

  if (paramsOnly) {
    const indexOfQuery = fullUrl.indexOf("?");

    if (indexOfQuery >= 0) {
      return fullUrl.substring(indexOfQuery);
    } else {
      return "";
    }
  }

  return fullUrl;
}

export function getAlertsUrlFromParams(
  url: string,
  filters: Partial<AlertFilterTypes>,
  query: any,
  paramsOnly = false
): string {
  const fullUrl = addTruthyParamsToUrl(url, {
    ...query,
    end_datetime: getDateWithTime(
      filters.dateFilter?.to,
      query?.end_datetime,
      filters?.dateFilter?.hasChange || false
    ),
    page: filters.page === 1 ? undefined : filters.page,
    start_datetime: getDateWithTime(
      filters.dateFilter?.from,
      query?.start_datetime,
      filters?.dateFilter?.hasChange || false
    ),
    alertTypes: convertAllAlertStatusToNone(filters.alertTypes).join(","),
  });

  if (paramsOnly) {
    const indexOfQuery = fullUrl.indexOf("?");

    if (indexOfQuery >= 0) {
      return fullUrl.substring(indexOfQuery);
    } else {
      return "";
    }
  }

  return fullUrl;
}

function getDateWithTime(
  dateString: string | undefined,
  queryDate: string | undefined,
  hasChange: boolean
) {
  // If the UI did't change the filter then return exactly what was in the query
  if (!hasChange) {
    return queryDate;
  }

  // If the dateString is undefined that means we are removing the current filter
  // so don't try to parse it
  return !dateString ? undefined : format(parseISO(dateString), "yyyy-MM-dd");
}
